import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Collapse from "@material-ui/core/Collapse";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import { CircularProgress, Icon } from "@material-ui/core";
import { isNil, isEmpty, size } from "lodash";
import { format, isValid } from "date-fns";

import { useStyles, commonIconStyles } from "./MicroserviceStatusStyles";
import useWindowDimensions from "../../../../core/hooks/useWindowDimensions";

const COLUMNS_PARENT = [
  {
    label: "Micro",
    align: "left",
  },
  {
    label: "Instances",
    align: "center",
  },
];

const COLUMNS_CHILD = [
  {
    label: "Version",
    align: "left",
  },
  {
    label: "IP",
    align: "left",
  },
  {
    label: "Port",
    align: "left",
  },
  {
    label: "Last Update",
    align: "left",
  },
  {
    label: "Status",
    align: "left",
  },
];

const useRowStyles = makeStyles({
  root: {
    "& > *": {
      borderBottom: "unset",
    },
  },
});

function Row(props) {
  const { row } = props;
  const [open, setOpen] = useState(false);
  const classes = useRowStyles();

  const totalAmount = row?.instance?.length;
  const errorAmount = row?.instance?.filter((r) => r?.status !== "UP")?.length;

  return (
    <>
      <TableRow className={classes.root}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="h6">{row?.name}</Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="h6" className={classes.summaryTypo}>
            {totalAmount}{" "}
            <Icon
              className={"fas fa-circle"}
              style={{
                ...commonIconStyles,
                fontSize: 10,
                marginBottom: -2,
                color: errorAmount === 0 ? "#55efc4" : "#e17055",
              }}
            />
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={COLUMNS_CHILD.length}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1} className={classes.innerInstancesTable}>
              <Typography variant="h6" gutterBottom component="div">
                Detail of instances
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {COLUMNS_CHILD.map(({ label, align }) => {
                      return (
                        <TableCell key={label}>
                          <Typography
                            align={align}
                            style={{ fontWeight: "bold" }}
                          >
                            {label}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.instance.map((instanceRow) => {
                    const d = new Date(+instanceRow?.lastUpdatedTimestamp);
                    const lastUpdate =
                      instanceRow?.lastUpdatedTimestamp && isValid(d)
                        ? format(d, "dd/MM/yyyy HH:mm:ss")
                        : "-";

                    const portToShow =
                      instanceRow?.metadata?.["port"] ||
                      instanceRow?.metadata?.["management.port"];

                    const versionToShow =
                      instanceRow?.metadata?.["version"] || "-";

                    return (
                      <TableRow key={instanceRow?.instanceId}>
                        <TableCell>{versionToShow}</TableCell>
                        <TableCell>{instanceRow?.ipAddr}</TableCell>
                        <TableCell>{portToShow}</TableCell>
                        <TableCell>{lastUpdate}</TableCell>
                        <TableCell>{instanceRow?.status}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function MicrosTable({ fetching, statusList, handleRefresh }) {
  const wd = useWindowDimensions();
  const classes = useStyles({ wd });

  return (
    <div className={classes.container}>
      <div className={classes.titleContainer}>
        <div className={classes.titleTextContainer}>
          <Icon className={"fas fa-signal"} style={commonIconStyles} />
          <Typography variant="h3">Status</Typography>
        </div>
        {fetching ? (
          <CircularProgress />
        ) : (
          <IconButton onClick={handleRefresh}>
            <Icon className={"fas fa-sync"} style={commonIconStyles} />
          </IconButton>
        )}
      </div>
      <div className={classes.tableContainer}>
        <Table stickyHeader>
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell />
              {COLUMNS_PARENT.map(({ label, align }, index) => {
                const valueAggr =
                  index === 0
                    ? size(statusList)
                    : index === 1
                    ? statusList?.reduce(
                        (acc, curr) => acc + size(curr.instance),
                        0
                      )
                    : null;

                return (
                  <TableCell key={label}>
                    <Typography variant="h4" align={align}>
                      {`${label} ${
                        !isNil(valueAggr) && valueAggr > 0
                          ? `(${valueAggr})`
                          : ""
                      }`}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {!isNil(statusList) &&
              !isEmpty(statusList) &&
              statusList.map((row) => <Row key={row.name} row={row} />)}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
